import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { CrudStyles } from '../../shared/Crud';
import { formatCurrencyBR, parseCurrencyCentsFromInput, formatPercentage } from '../../../utils/masks';
import deliveryService from '../../../services/deliveryService';
import MessageModal from '../../shared/MessageModal';
import DeliveryCostCenterSelectorModal from './DeliveryCostCenterSelectorModal';
import DeliveryDriverSection from './DeliveryDriverSection';
import DeliveryRequesterSection from './DeliveryRequesterSection';
import { isLegalPersonCustomer } from '../../../constants/customerType';

const { SectionTitle, FormGroup, Input, Button } = CrudStyles;

const SectionBox = styled.div`
    border-radius: 12px;
    padding: 24px;
    margin-bottom: 20px;
    display: flex;
    flex-direction: column;
    gap: 20px;
    background: ${({ theme }) => theme?.colors?.primary?.light ? `${theme.colors.primary.light}08` : 'rgba(59, 130, 246, 0.04)'};
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
    border: 1px solid ${({ theme }) => theme?.colors?.primary?.light ? `${theme.colors.primary.light}40` : 'rgba(59, 130, 246, 0.12)'};
`;

const SectionTitleSoft = styled(SectionTitle)`
    font-size: 15px;
    font-weight: 600;
    color: ${({ theme }) => theme?.colors?.gray?.[800] || '#374151'};
    margin-bottom: 12px;
    margin-top: 0;
`;

const TaxasHeading = styled.p`
    font-size: 13px;
    font-weight: 600;
    color: ${({ theme }) => theme?.colors?.gray?.[700] || '#374151'};
    margin: 20px 0 12px 0;
`;

const FeeCard = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 20px;
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    margin-bottom: 16px;
    transition: border-color 0.15s, box-shadow 0.15s;
    &:hover {
        border-color: #d1d5db;
        box-shadow: 0 1px 3px rgba(0,0,0,0.05);
    }
`;

const FeeCardRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
    align-items: flex-start;
    & > .field { flex: 1 1 180px; min-width: 0; }
    & > .field-narrow { flex: 0 1 140px; min-width: 0; }
`;

const FeeCardActions = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;
    padding-top: 8px;
    border-top: 1px solid ${({ theme }) => theme?.colors?.gray?.[200] || '#e5e7eb'};
`;

const FeeRemoveBtn = styled.button`
    width: 36px;
    height: 36px;
    border: none;
    border-radius: 8px;
    background: #fef2f2;
    color: #b91c1c;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
    line-height: 1;
    transition: background 0.15s, color 0.15s;
    &:hover {
        background: #fee2e2;
        color: #991b1b;
    }
`;

const BaseReadOnly = styled.span`
    font-size: 14px;
    color: ${({ theme }) => theme?.colors?.gray?.[600] || '#6b7280'};
    padding: 8px 12px;
    background: ${({ theme }) => theme?.colors?.gray?.[50] || '#f9fafb'};
    border-radius: 8px;
    display: block;
`;

const AddFeeButton = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 12px 20px;
    border: 2px dashed #d1d5db;
    border-radius: 10px;
    background: #fafafa;
    color: #6b7280;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: border-color 0.15s, background 0.15s, color 0.15s;
    width: 100%;
    justify-content: center;
    &:hover:not(:disabled) {
        border-color: ${({ theme }) => theme?.colors?.primary?.main || '#3b82f6'};
        background: ${({ theme }) => theme?.colors?.primary?.light ? `${theme.colors.primary.light}12` : 'rgba(59, 130, 246, 0.08)'};
        color: ${({ theme }) => theme?.colors?.primary?.main || '#2563eb'};
    }
    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
`;

const FeeInput = styled.input`
    padding: 8px 12px;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    font-size: 14px;
    width: 100%;
    &:focus {
        outline: none;
        border-color: ${({ theme }) => theme?.colors?.primary?.main || '#3b82f6'};
        box-shadow: 0 0 0 2px ${({ theme }) => theme?.colors?.primary?.light ? `${theme.colors.primary.light}40` : 'rgba(59, 130, 246, 0.2)'};
    }
`;

const EmptyFeesHint = styled.p`
    font-size: 13px;
    color: #9ca3af;
    margin: 0 0 12px 0;
`;

const UnsavedFeesCallout = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px 16px;
    margin-top: 8px;
    border-radius: 10px;
    background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
    border: 1px solid #f59e0b;
    color: #92400e;
    font-size: 14px;
    font-weight: 600;
    box-shadow: 0 2px 8px rgba(245, 158, 11, 0.25);
`;

const UnsavedHint = styled.span`
    display: inline-block;
    margin-left: 8px;
    padding: 2px 8px;
    border-radius: 6px;
    background: #fef3c7;
    color: #92400e;
    font-size: 12px;
    font-weight: 600;
`;

const ReadOnlyLine = styled.p`
    margin: 0 0 8px;
    font-size: 14px;
    color: ${({ theme }) => theme?.colors?.gray?.[700] || '#374151'};
`;

const FeeRow = styled.div`
    display: flex;
    justify-content: space-between;
    padding: 8px 0;
    border-bottom: 1px solid #e5e7eb;
    font-size: 14px;
    color: #374151;
    &:last-of-type {
        border-bottom: none;
    }
`;

const TotalRow = styled.div`
    display: flex;
    justify-content: space-between;
    padding: 14px 0 0;
    margin-top: 12px;
    border-top: 2px solid ${({ theme }) => theme?.colors?.gray?.[300] || '#d1d5db'};
    font-size: 15px;
    color: ${({ theme }) => theme?.colors?.gray?.[800] || '#1f2937'};
`;

const CostCenterHint = styled.span`
    color: #b91c1c;
    font-size: 13px;
`;

/** Card separado da seção de valores — mesmo container que `SectionBox`; título/ajuda alinhados a `SectionTitleSoft` + texto secundário. */
const CostCenterSection = styled.section`
    border-radius: 12px;
    padding: 24px;
    margin-top: 20px;
    margin-bottom: 20px;
    display: flex;
    flex-direction: column;
    gap: 16px;
    background: ${({ theme }) => theme?.colors?.primary?.light ? `${theme.colors.primary.light}08` : 'rgba(59, 130, 246, 0.04)'};
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
    border: 1px solid ${({ theme }) => theme?.colors?.primary?.light ? `${theme.colors.primary.light}40` : 'rgba(59, 130, 246, 0.12)'};
`;

const CostCenterTitleRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
`;

const CostCenterTitle = styled(SectionTitleSoft)`
    width: 100%;
`;

const CostCenterHelp = styled.p`
    margin: 8px 0 0;
    font-size: 13px;
    line-height: 1.5;
    color: ${({ theme }) => theme?.colors?.gray?.[600] || '#6b7280'};
    max-width: 42rem;
`;

const CostCenterGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
    gap: 12px;
`;

const CostCenterCell = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 12px 14px;
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    min-width: 0;
`;

const CostCenterCellLabel = styled.span`
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #64748b;
`;

const CostCenterCellValue = styled.span`
    font-size: 14px;
    font-weight: 500;
    color: #0f172a;
    word-break: break-word;
`;

const CostCenterStatusBadge = styled.span`
    display: inline-flex;
    align-items: center;
    width: fit-content;
    padding: 4px 10px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 600;
    background: ${({ $active }) => ($active ? '#d1fae5' : '#f1f5f9')};
    color: ${({ $active }) => ($active ? '#065f46' : '#64748b')};
    border: 1px solid ${({ $active }) => ($active ? '#6ee7b7' : '#e2e8f0')};
`;

const CostCenterEmptyState = styled.div`
    padding: 18px 16px;
    text-align: center;
    border-radius: 10px;
    border: 1px dashed #cbd5e1;
    background: rgba(255, 255, 255, 0.7);
    color: #94a3b8;
    font-size: 14px;
    font-weight: 500;
`;

const CostCenterActions = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    justify-content: flex-end;
    align-items: center;
`;

const emptyFee = () => ({
    id: `fee-${Date.now()}-${Math.random().toString(36).slice(2)}`,
    description: '',
    value: null,
    quantity: null,
    isPercentage: false,
});

export default function ValoresStep({
    deliveryId,
    customerIdForApi,
    deliveryEntityCustomerId = null,
    customerType = null,
    driverId = null,
    driverName = '',
    onDriverChange,
    deliveryValue,
    deliveryBillDescription = '',
    deliveryBillId,
    billEditable = true,
    savedDeliveryValue,
    savedDeliveryBillDescription,
    fees = [],
    onDeliveryValueChange,
    onDeliveryBillDescriptionChange,
    onBillSaved,
    onFeesChange,
    onFeeSaved,
    onFeeUpdated,
    isTransportCompany = true,
    requesterId = null,
    requesterName = '',
    requesterEmail = '',
    onRequesterChange,
    dataRefreshKey = 0,
}) {
    const [saving, setSaving] = useState(false);
    const [savingFeeIndex, setSavingFeeIndex] = useState(null);
    const [messageModal, setMessageModal] = useState({ open: false, title: 'Aviso', message: '' });

    const [costCenterModalOpen, setCostCenterModalOpen] = useState(false);
    const [costCenterHeader, setCostCenterHeader] = useState(null);
    const [costCenterLoading, setCostCenterLoading] = useState(false);
    const [costCenterLoadError, setCostCenterLoadError] = useState(null);
    const [updatingCostCenter, setUpdatingCostCenter] = useState(false);

    // Centro de custo só para PJ explícito — tipo desconhecido/PF não exibe nem consulta.
    const showCostCenter = isLegalPersonCustomer(customerType);

    const costCenterButtonLabel = useMemo(() => {
        return costCenterHeader?.id != null
            ? 'Selecionar/alterar centro de custo'
            : 'Selecionar centro de custo';
    }, [costCenterHeader]);

    const loadCostCenterHeader = async () => {
        if (!deliveryId || !showCostCenter) return;
        setCostCenterLoading(true);
        setCostCenterLoadError(null);
        try {
            const dto = await deliveryService.getDeliveryCostCenter(deliveryId, customerIdForApi);
            setCostCenterHeader(dto || null);
        } catch (err) {
            setCostCenterHeader(null);
            setCostCenterLoadError(err?.message || 'Não foi possível carregar o centro de custo atual da entrega.');
        } finally {
            setCostCenterLoading(false);
        }
    };

    const handleSaveBill = async () => {
        if (!deliveryId) {
            setMessageModal({ open: true, title: 'Aviso', message: 'Crie a entrega antes de salvar a cobrança.' });
            return;
        }
        setSaving(true);
        try {
            const result = await deliveryService.updateDeliveryBill(
                deliveryId,
                {
                    amount: deliveryValue ?? 0,
                    description: deliveryBillDescription || null,
                },
                customerIdForApi
            );
            if (result?.id != null) {
                onBillSaved?.(result.id);
            }
        } catch (err) {
            setMessageModal({
                open: true,
                title: 'Erro',
                message: err?.message || 'Erro ao salvar valor da entrega.',
            });
        } finally {
            setSaving(false);
        }
    };

    const handleAddFee = () => {
        onFeesChange?.([emptyFee(), ...(fees || [])]);
    };

    const handleRemoveFee = async (index) => {
        const fee = (fees || [])[index];
        // Taxa ainda não persistida: remove só no estado local
        if (!fee || fee.feeId == null) {
            onFeesChange?.(fees.filter((_, i) => i !== index));
            return;
        }
        try {
            await deliveryService.deleteFee(deliveryId, fee.feeId, customerIdForApi);
            onFeesChange?.(fees.filter((_, i) => i !== index));
        } catch (err) {
            setMessageModal({
                open: true,
                title: 'Erro',
                message: err?.message || 'Erro ao remover taxa.',
            });
        }
    };

    const handleSaveFee = async (index) => {
        const fee = (fees || [])[index];
        if (!fee) return;
        if (!deliveryId) {
            setMessageModal({ open: true, title: 'Aviso', message: 'Crie a entrega e salve o valor da entrega antes de salvar a taxa.' });
            return;
        }
        const desc = (fee.description ?? '').trim();
        if (desc === '') {
            setMessageModal({ open: true, title: 'Aviso', message: 'Informe a descrição da taxa.' });
            return;
        }
        const deliveryReais = (deliveryValue != null && deliveryValue !== '') ? Number(deliveryValue) : 0;
        const isPct = !!fee.isPercentage;
        const amount = fee.value != null ? Number(fee.value) : 0;
        if (amount <= 0) {
            setMessageModal({ open: true, title: 'Aviso', message: isPct ? 'Informe o valor da porcentagem.' : 'Informe o valor da taxa.' });
            return;
        }
        if (!isPct && (fee.quantity == null || Number(fee.quantity) < 1)) {
            setMessageModal({ open: true, title: 'Aviso', message: 'Para taxa em valor fixo, informe a quantidade (mín. 1).' });
            return;
        }
        const payload = {
            calculationBasis: isPct ? deliveryReais : Math.max(1, Number(fee.quantity) || 1),
            amount,
            description: desc,
            percentage: isPct,
        };
        setSavingFeeIndex(index);
        try {
            if (fee.feeId != null) {
                await deliveryService.updateFee(deliveryId, fee.feeId, payload, customerIdForApi);
                onFeeUpdated?.(index, payload);
            } else {
                const result = await deliveryService.createFee(deliveryId, payload, customerIdForApi);
                if (result?.id != null) {
                    onFeeSaved?.(index, result.id, payload);
                }
            }
        } catch (err) {
            setMessageModal({
                open: true,
                title: 'Erro',
                message: err?.message || (fee.feeId != null ? 'Erro ao atualizar taxa.' : 'Erro ao salvar taxa.'),
            });
        } finally {
            setSavingFeeIndex(null);
        }
    };

    const handleFeeChange = (index, field, value) => {
        const next = (fees || []).map((f, i) => {
            if (i !== index) return f;
            const updated = { ...f, [field]: value };
            if (field === 'isPercentage') {
                updated.quantity = value ? null : (f.quantity ?? null);
            }
            return updated;
        });
        onFeesChange?.(next);
    };

    const feesList = fees?.length ? fees : [];

    function feeHasUnsavedChanges(fee) {
        if (fee.feeId == null) return true;
        const snap = fee.savedSnapshot;
        if (!snap) return false;
        const descEq = (fee.description ?? '').trim() === (snap.description ?? '').trim();
        const amountEq = Number(fee.value) === Number(snap.amount);
        const pctEq = !!fee.isPercentage === !!snap.percentage;
        const basisEq = fee.isPercentage
            ? true
            : Math.max(1, Number(fee.quantity) || 1) === Number(snap.calculationBasis);
        return !(descEq && amountEq && pctEq && basisEq);
    }

    const hasUnpersistedFees = feesList.some((f) => f.feeId == null);
    const hasFeesWithUnsavedUpdates = feesList.some((f) => f.feeId != null && feeHasUnsavedChanges(f));
    const mainBillUnsaved =
        (deliveryBillId == null && ((deliveryValue != null && deliveryValue !== '') || (deliveryBillDescription ?? '').trim() !== '')) ||
        (deliveryBillId != null && (Number(deliveryValue) !== Number(savedDeliveryValue) || (deliveryBillDescription ?? '').trim() !== (savedDeliveryBillDescription ?? '').trim()));

    useEffect(() => {
        if (!showCostCenter) {
            setCostCenterHeader(null);
            setCostCenterLoadError(null);
            setCostCenterModalOpen(false);
            return;
        }
        loadCostCenterHeader();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deliveryId, customerIdForApi, showCostCenter, dataRefreshKey]);

    const handleSelectCostCenter = async (cc) => {
        const costCenterId = cc?.id;
        if (costCenterId == null) return;
        if (!deliveryId) {
            setMessageModal({ open: true, title: 'Aviso', message: 'Crie a entrega antes de selecionar o centro de custo.' });
            return;
        }

        setUpdatingCostCenter(true);
        try {
            await deliveryService.updateDeliveryCostCenter(deliveryId, costCenterId, customerIdForApi);
            await loadCostCenterHeader();
            setCostCenterModalOpen(false);
        } catch (err) {
            setMessageModal({
                open: true,
                title: 'Erro',
                message: err?.message || 'Erro ao atualizar o centro de custo da entrega.',
            });
        } finally {
            setUpdatingCostCenter(false);
        }
    };

    const handleRemoveCostCenter = async () => {
        if (!deliveryId) return;
        setUpdatingCostCenter(true);
        try {
            await deliveryService.updateDeliveryCostCenter(deliveryId, null, customerIdForApi);
            await loadCostCenterHeader();
            setCostCenterModalOpen(false);
        } catch (err) {
            setMessageModal({
                open: true,
                title: 'Erro',
                message: err?.message || 'Erro ao remover o centro de custo da entrega.',
            });
        } finally {
            setUpdatingCostCenter(false);
        }
    };

    return (
        <>
            {billEditable ? (
            <SectionBox>
            <SectionTitleSoft>Valores da entrega</SectionTitleSoft>
            <FormGroup>
                <label>
                    Valor da entrega
                    {mainBillUnsaved && <UnsavedHint>Não salvo</UnsavedHint>}
                </label>
                <Input
                    type="text"
                    inputMode="decimal"
                    value={formatCurrencyBR(deliveryValue)}
                    onChange={(e) => onDeliveryValueChange?.(parseCurrencyCentsFromInput(e.target.value))}
                    placeholder="R$ 0,00"
                />
            </FormGroup>
            <FormGroup>
                <label>Descrição da cobrança (opcional, pode ser editada posteriormente e será exibida na fatura)</label>
                <Input
                    type="text"
                    value={deliveryBillDescription ?? ''}
                    onChange={(e) => onDeliveryBillDescriptionChange?.(e.target.value)}
                    placeholder="Ex.: Valor do frete"
                />
            </FormGroup>
            <FormGroup style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                    type="button"
                    variant="primary"
                    onClick={handleSaveBill}
                    disabled={saving}
                    style={{ justifyContent: 'center' }}
                >
                    {saving ? 'Salvando...' : deliveryBillId ? 'Atualizar' : 'Salvar'}
                </Button>
            </FormGroup>
            <TaxasHeading>Taxas adicionais</TaxasHeading>
            <AddFeeButton
                type="button"
                onClick={handleAddFee}
                disabled={!deliveryBillId}
                title={!deliveryBillId ? 'Salve o valor da entrega antes de adicionar taxas.' : ''}
            >
                + Nova taxa
            </AddFeeButton>
            {feesList.length === 0 ? (
                <EmptyFeesHint>Nenhuma taxa. As taxas adicionadas aparecerão abaixo do botão.</EmptyFeesHint>
            ) : (
                <div style={{ marginTop: '12px', width: '100%' }}>
                    {feesList.map((fee, index) => {
                        const isPct = !!fee.isPercentage;
                        const deliveryValueReais = (deliveryValue != null && deliveryValue !== '') ? Number(deliveryValue) : 0;
                        return (
                            <FeeCard key={fee.id}>
                                {feeHasUnsavedChanges(fee) && (
                                    <UnsavedHint style={{ marginBottom: 0, alignSelf: 'flex-start' }}>Não salva</UnsavedHint>
                                )}
                                <FeeCardRow>
                                    <FormGroup className="field">
                                        <label>Descrição da taxa</label>
                                        <FeeInput
                                            value={fee.description ?? ''}
                                            onChange={(e) => handleFeeChange(index, 'description', e.target.value)}
                                            placeholder="Ex.: Taxa de seguro"
                                            aria-label="Descrição da taxa"
                                        />
                                    </FormGroup>
                                    <FormGroup className="field-narrow">
                                        <label>Tipo da taxa</label>
                                        <select
                                        value={fee.isPercentage ? 'percent' : 'fixed'}
                                        onChange={(e) => handleFeeChange(index, 'isPercentage', e.target.value === 'percent')}
                                        style={{
                                            width: '100%',
                                            padding: '8px 12px',
                                            border: '1px solid #e5e7eb',
                                            borderRadius: 8,
                                            fontSize: 14,
                                        }}
                                    >
                                            <option value="fixed">Valor fixo</option>
                                            <option value="percent">Porcentagem</option>
                                        </select>
                                    </FormGroup>
                                </FeeCardRow>
                                {isPct ? (
                                    <FormGroup>
                                        <label>Base de cálculo</label>
                                        <BaseReadOnly>
                                            {formatCurrencyBR(deliveryValueReais)}
                                        </BaseReadOnly>
                                    </FormGroup>
                                ) : (
                                    <FormGroup className="field-narrow">
                                        <label>Quantidade (base de cálculo)</label>
                                        <FeeInput
                                            type="number"
                                            min="0"
                                            step="1"
                                            value={fee.quantity ?? ''}
                                            onChange={(e) => {
                                                const v = e.target.value;
                                                handleFeeChange(index, 'quantity', v === '' ? null : (parseFloat(v) || null));
                                            }}
                                            placeholder="Ex.: 1"
                                            aria-label="Quantidade da taxa"
                                        />
                                    </FormGroup>
                                )}
                                <FormGroup className="field-narrow">
                                    <label>{isPct ? 'Valor (%)' : 'Valor (R$)'}</label>
                                    <FeeInput
                                        type="text"
                                        inputMode="decimal"
                                        value={isPct ? formatPercentage(fee.value) : formatCurrencyBR(fee.value)}
                                        onChange={(e) => {
                                            const raw = isPct
                                                ? parseCurrencyCentsFromInput(e.target.value, 5)
                                                : parseCurrencyCentsFromInput(e.target.value);
                                            const v = isPct && raw != null
                                                ? Math.min(1000, Math.max(0, raw))
                                                : raw;
                                            handleFeeChange(index, 'value', v);
                                        }}
                                        placeholder={isPct ? 'Ex.: 10,50 (máx. 1000%)' : 'R$ 0,00'}
                                        aria-label={isPct ? 'Porcentagem da taxa' : 'Valor da taxa'}
                                    />
                                </FormGroup>
                                <FormGroup>
                                    <label>Valor total da taxa</label>
                                    <BaseReadOnly>
                                        {formatCurrencyBR(
                                            isPct
                                                ? (deliveryValueReais * (fee.value ?? 0)) / 100
                                                : (fee.value ?? 0) * Math.max(1, fee.quantity ?? 0)
                                        )}
                                    </BaseReadOnly>
                                </FormGroup>
                                <FeeCardActions>
                                    <FeeRemoveBtn
                                        type="button"
                                        onClick={() => handleRemoveFee(index)}
                                        title="Remover taxa"
                                        aria-label="Remover taxa"
                                    >
                                        ×
                                    </FeeRemoveBtn>
                                    <Button
                                        type="button"
                                        variant="primary"
                                        disabled={savingFeeIndex === index}
                                        onClick={() => handleSaveFee(index)}
                                        style={{ justifyContent: 'center' }}
                                    >
                                        {savingFeeIndex === index ? 'Salvando...' : fee.feeId != null ? 'Atualizar' : 'Salvar'}
                                    </Button>
                                </FeeCardActions>
                            </FeeCard>
                        );
                    })}
                </div>
            )}

            {(() => {
                const deliveryReais = (deliveryValue != null && deliveryValue !== '') ? Number(deliveryValue) : 0;
                const persistedFees = (feesList || []).filter((f) => f.feeId != null);
                const feesTotal = persistedFees.reduce((sum, fee) => {
                    const snap = fee.savedSnapshot;
                    if (snap) {
                        const isPct = !!snap.percentage;
                        const contrib = isPct
                            ? (Number(snap.calculationBasis) * Number(snap.amount)) / 100
                            : Number(snap.amount) * Math.max(1, Number(snap.calculationBasis));
                        return sum + contrib;
                    }
                    const isPct = !!fee.isPercentage;
                    const contrib = isPct
                        ? (deliveryReais * (fee.value ?? 0)) / 100
                        : (fee.value ?? 0) * Math.max(1, fee.quantity ?? 0);
                    return sum + contrib;
                }, 0);
                const totalEntrega = deliveryReais + feesTotal;
                return (
                    <>
                        <FormGroup style={{ marginTop: 24, paddingTop: 20, borderTop: '1px solid #e5e7eb' }}>
                            <label style={{ fontWeight: 600, fontSize: 15 }}>Valor total da entrega</label>
                            <BaseReadOnly style={{ fontSize: 16, fontWeight: 600 }}>
                                {formatCurrencyBR(totalEntrega)}
                            </BaseReadOnly>
                        </FormGroup>
                        {(hasUnpersistedFees || hasFeesWithUnsavedUpdates) && (
                            <UnsavedFeesCallout>
                                Existem taxas em preenchimento ou com alterações não salvas. O valor total considera apenas o que já foi salvo.
                            </UnsavedFeesCallout>
                        )}
                    </>
                );
            })()}
            </SectionBox>
            ) : (
            <SectionBox>
                <SectionTitleSoft>Valores</SectionTitleSoft>
                <ReadOnlyLine>
                    <strong>Valor da entrega:</strong>{' '}
                    {deliveryValue != null ? formatCurrencyBR(deliveryValue) : '—'}
                </ReadOnlyLine>
                {deliveryBillDescription ? (
                    <ReadOnlyLine>
                        <strong>Descrição da cobrança:</strong> {deliveryBillDescription}
                    </ReadOnlyLine>
                ) : null}
                {(() => {
                    const persistedFees = (fees || []).filter((f) => f.feeId != null);
                    if (persistedFees.length === 0) return null;
                    const deliveryReais = (deliveryValue != null && deliveryValue !== '') ? Number(deliveryValue) : 0;
                    return (
                        <>
                            <ReadOnlyLine style={{ marginTop: '16px', marginBottom: '8px' }}>
                                <strong>Taxas:</strong>
                            </ReadOnlyLine>
                            {persistedFees.map((fee, i) => {
                                const isPct = !!fee.isPercentage;
                                const feeTotal = isPct
                                    ? (deliveryReais * (fee.value ?? 0)) / 100
                                    : (fee.value ?? 0) * Math.max(1, fee.quantity ?? 0);
                                return (
                                    <FeeRow key={fee.feeId ?? fee.id ?? i}>
                                        <span>{fee.description || 'Taxa'}</span>
                                        <span>{formatCurrencyBR(feeTotal)}</span>
                                    </FeeRow>
                                );
                            })}
                        </>
                    );
                })()}
                {(() => {
                    const deliveryReais = (deliveryValue != null && deliveryValue !== '') ? Number(deliveryValue) : 0;
                    const persistedFees = (fees || []).filter((f) => f.feeId != null);
                    const total = deliveryReais + persistedFees.reduce((sum, fee) => {
                        const isPct = !!fee.isPercentage;
                        return sum + (isPct
                            ? (deliveryReais * (fee.value ?? 0)) / 100
                            : (fee.value ?? 0) * Math.max(1, fee.quantity ?? 0));
                    }, 0);
                    return (
                        <TotalRow>
                            <span><strong>Valor total</strong></span>
                            <span><strong>{formatCurrencyBR(total)}</strong></span>
                        </TotalRow>
                    );
                })()}
            </SectionBox>
            )}

            <DeliveryDriverSection
                deliveryId={deliveryId}
                customerIdForApi={customerIdForApi}
                deliveryEntityCustomerId={deliveryEntityCustomerId}
                driverId={driverId}
                driverName={driverName}
                onDriverChange={onDriverChange}
            />

            {showCostCenter ? (
                <CostCenterSection aria-labelledby="delivery-cost-center-title">
                    <CostCenterTitleRow>
                        <div>
                            <CostCenterTitle id="delivery-cost-center-title">Centro de custo</CostCenterTitle>
                            <CostCenterHelp>
                                Se o solicitante já fizer parte de um centro de custo, a entrega já pode vir com um
                                centro atribuído. Use o botão abaixo para selecionar ou alterar.
                            </CostCenterHelp>
                        </div>
                    </CostCenterTitleRow>

                    {costCenterLoading ? (
                        <CostCenterEmptyState>Carregando centro de custo…</CostCenterEmptyState>
                    ) : costCenterLoadError ? (
                        <div>
                            <CostCenterHint>{costCenterLoadError}</CostCenterHint>
                            <CostCenterActions style={{ justifyContent: 'flex-start', marginTop: 12 }}>
                                <Button type="button" variant="outline" onClick={loadCostCenterHeader} disabled={costCenterLoading}>
                                    Tentar novamente
                                </Button>
                            </CostCenterActions>
                        </div>
                    ) : costCenterHeader?.id != null ? (
                        <CostCenterGrid>
                            <CostCenterCell>
                                <CostCenterCellLabel>ID</CostCenterCellLabel>
                                <CostCenterCellValue>{String(costCenterHeader.id)}</CostCenterCellValue>
                            </CostCenterCell>
                            <CostCenterCell>
                                <CostCenterCellLabel>Nome</CostCenterCellLabel>
                                <CostCenterCellValue>{costCenterHeader.name || '—'}</CostCenterCellValue>
                            </CostCenterCell>
                            <CostCenterCell>
                                <CostCenterCellLabel>Código</CostCenterCellLabel>
                                <CostCenterCellValue>{costCenterHeader.code || '—'}</CostCenterCellValue>
                            </CostCenterCell>
                            <CostCenterCell>
                                <CostCenterCellLabel>Situação</CostCenterCellLabel>
                                <CostCenterStatusBadge $active={!!costCenterHeader.active}>
                                    {costCenterHeader.active ? 'Ativo' : 'Inativo'}
                                </CostCenterStatusBadge>
                            </CostCenterCell>
                        </CostCenterGrid>
                    ) : (
                        <CostCenterEmptyState>Nenhum centro de custo atribuído</CostCenterEmptyState>
                    )}

                    <CostCenterActions>
                        <Button
                            type="button"
                            variant="outline"
                            disabled={!deliveryId || updatingCostCenter}
                            onClick={() => setCostCenterModalOpen(true)}
                            title="Selecionar ou alterar centro de custo"
                        >
                            {updatingCostCenter ? 'Alterando…' : costCenterButtonLabel}
                        </Button>
                    </CostCenterActions>
                </CostCenterSection>
            ) : null}

            <DeliveryRequesterSection
                deliveryId={deliveryId}
                customerId={customerIdForApi}
                isTransportCompany={isTransportCompany}
                requesterId={requesterId}
                requesterName={requesterName}
                requesterEmail={requesterEmail}
                onRequesterChange={onRequesterChange}
            />

            {messageModal.open && (
                <MessageModal
                    title={messageModal.title}
                    message={messageModal.message}
                    onClose={() => setMessageModal((m) => ({ ...m, open: false }))}
                />
            )}

            {showCostCenter ? (
                <DeliveryCostCenterSelectorModal
                    open={costCenterModalOpen}
                    customerId={customerIdForApi}
                    currentCostCenterId={costCenterHeader?.id ?? null}
                    isSubmitting={updatingCostCenter}
                    onClose={() => setCostCenterModalOpen(false)}
                    onSelectCostCenter={handleSelectCostCenter}
                    onRemoveCostCenter={handleRemoveCostCenter}
                />
            ) : null}
        </>
    );
}
